// The real backend. Every call goes through PostgREST under the signed-in user's JWT, so
// RLS decides what is visible and writable — there is deliberately no service-role path
// in this app.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { DataSourceError, type DataSource } from './datasource';
import type {
    AppRole, CurrentUser, Exercise, MediaAsset, Module, ModuleVersion, Program,
    ProgramNode, Session, Skill, SkillStudy, Uuid,
} from './types';

export class SupabaseDataSource implements DataSource {
    readonly label = 'Supabase';
    private client: SupabaseClient;

    constructor(url: string, anonKey: string) {
        this.client = createClient(url, anonKey);
    }

    private unwrap<T>(result: { data: T | null; error: { message: string } | null }, what: string): T {
        if (result.error) throw new DataSourceError(`${what}: ${result.error.message}`, result.error);
        if (result.data === null) throw new DataSourceError(`${what}: no data returned`);
        return result.data;
    }

    // --- Auth --------------------------------------------------------------------

    async signIn(email: string, password: string): Promise<CurrentUser> {
        const { error } = await this.client.auth.signInWithPassword({ email, password });
        if (error) throw new DataSourceError(error.message, error);
        const user = await this.currentUser();
        if (!user) throw new DataSourceError('Signed in but no profile found');
        return user;
    }

    async signOut(): Promise<void> {
        await this.client.auth.signOut();
    }

    async currentUser(): Promise<CurrentUser | null> {
        const { data: sessionData } = await this.client.auth.getSession();
        const authUser = sessionData.session?.user;
        if (!authUser) return null;

        // Both reads are RLS-filtered to this user, so no scoping is needed here.
        const [{ data: roles }, { data: owned }] = await Promise.all([
            this.client.from('user_roles').select('role'),
            this.client.from('module_owners').select('module_id'),
        ]);

        return {
            id: authUser.id,
            email: authUser.email ?? '',
            roles: (roles ?? []).map((r) => r.role as AppRole),
            ownedModuleIds: (owned ?? []).map((r) => r.module_id as Uuid),
        };
    }

    // --- Modules -----------------------------------------------------------------

    async listModules(): Promise<Module[]> {
        return this.unwrap(
            await this.client.from('modules').select('*').order('name'),
            'list modules',
        ) as Module[];
    }

    async getModule(id: Uuid): Promise<Module | null> {
        const { data } = await this.client.from('modules').select('*').eq('id', id).maybeSingle();
        return (data as Module) ?? null;
    }

    async createModule(input: Pick<Module, 'slug' | 'name' | 'studio_name' | 'category' | 'difficulty'>): Promise<Module> {
        const rows = this.unwrap(
            await this.client.from('modules').insert(input).select(),
            'create module',
        ) as Module[];
        return rows[0]!;
    }

    async updateModule(id: Uuid, patch: Partial<Module>): Promise<void> {
        const { error } = await this.client.from('modules').update(patch).eq('id', id);
        if (error) throw new DataSourceError(`update module: ${error.message}`, error);
    }

    // --- Generic helpers ---------------------------------------------------------

    private async list<T>(table: string, moduleId: Uuid, orderBy: string): Promise<T[]> {
        return this.unwrap(
            await this.client.from(table).select('*').eq('module_id', moduleId).order(orderBy),
            `list ${table}`,
        ) as T[];
    }

    private async upsert<T>(table: string, row: Record<string, unknown>): Promise<T> {
        const rows = this.unwrap(
            await this.client.from(table).upsert(row).select(),
            `save ${table}`,
        ) as T[];
        return rows[0]!;
    }

    private async remove(table: string, id: Uuid): Promise<void> {
        const { error } = await this.client.from(table).delete().eq('id', id);
        if (error) {
            // A foreign-key violation here is the derived link tables doing their job:
            // something still references this row. Say so plainly rather than leaking
            // the constraint name.
            const friendly = error.message.includes('violates foreign key')
                ? 'Still referenced by other content — remove those references first.'
                : error.message;
            throw new DataSourceError(friendly, error);
        }
    }

    // --- Content -----------------------------------------------------------------

    listExercises = (moduleId: Uuid) => this.list<Exercise>('exercises', moduleId, 'name');
    upsertExercise = (e: Partial<Exercise> & { module_id: Uuid }) => this.upsert<Exercise>('exercises', e);
    deleteExercise = (id: Uuid) => this.remove('exercises', id);

    listSessions = (moduleId: Uuid) => this.list<Session>('sessions', moduleId, 'title');
    upsertSession = (s: Partial<Session> & { module_id: Uuid }) => this.upsert<Session>('sessions', s);
    deleteSession = (id: Uuid) => this.remove('sessions', id);

    listSkillStudies = (moduleId: Uuid) => this.list<SkillStudy>('skill_studies', moduleId, 'sort_order');
    upsertSkillStudy = (s: Partial<SkillStudy> & { module_id: Uuid }) => this.upsert<SkillStudy>('skill_studies', s);
    deleteSkillStudy = (id: Uuid) => this.remove('skill_studies', id);

    // --- Programme ---------------------------------------------------------------

    async getProgram(moduleId: Uuid): Promise<Program | null> {
        const { data } = await this.client.from('programs').select('*').eq('module_id', moduleId).maybeSingle();
        return (data as Program) ?? null;
    }

    async upsertProgram(program: Program): Promise<void> {
        const { error } = await this.client.from('programs').upsert(program);
        if (error) throw new DataSourceError(`save programme: ${error.message}`, error);
    }

    listProgramNodes = (moduleId: Uuid) => this.list<ProgramNode>('program_nodes', moduleId, 'sort_order');
    upsertProgramNode = (n: Partial<ProgramNode> & { module_id: Uuid }) => this.upsert<ProgramNode>('program_nodes', n);
    deleteProgramNode = (id: Uuid) => this.remove('program_nodes', id);

    async reorderProgramNodes(rows: { id: Uuid; sort_order: number }[]): Promise<void> {
        // Sequential rather than a bulk upsert: an upsert would need every non-null column
        // and could clobber concurrent edits to titles.
        for (const row of rows) {
            const { error } = await this.client
                .from('program_nodes').update({ sort_order: row.sort_order }).eq('id', row.id);
            if (error) throw new DataSourceError(`reorder: ${error.message}`, error);
        }
    }

    // --- Skills ------------------------------------------------------------------

    async listSkills(moduleId: Uuid): Promise<Skill[]> {
        const skills = await this.list<Skill>('skills', moduleId, 'tier');
        const { data: prereqs } = await this.client
            .from('skill_prerequisites')
            .select('skill_id, prerequisite_id')
            .in('skill_id', skills.map((s) => s.id));

        return skills.map((skill) => ({
            ...skill,
            prerequisites: (prereqs ?? [])
                .filter((p) => p.skill_id === skill.id)
                .map((p) => p.prerequisite_id as Uuid),
        }));
    }

    async upsertSkill(skill: Partial<Skill> & { module_id: Uuid }): Promise<Skill> {
        const { prerequisites, ...row } = skill;
        const saved = await this.upsert<Skill>('skills', row);

        if (prerequisites) {
            await this.client.from('skill_prerequisites').delete().eq('skill_id', saved.id);
            if (prerequisites.length > 0) {
                const { error } = await this.client.from('skill_prerequisites').insert(
                    prerequisites.map((p) => ({ skill_id: saved.id, prerequisite_id: p })),
                );
                if (error) throw new DataSourceError(`save prerequisites: ${error.message}`, error);
            }
        }
        return { ...saved, prerequisites: prerequisites ?? [] };
    }

    deleteSkill = (id: Uuid) => this.remove('skills', id);

    // --- Media -------------------------------------------------------------------

    async listMedia(moduleId: Uuid): Promise<MediaAsset[]> {
        return this.list<MediaAsset>('media_assets', moduleId, 'created_at');
    }

    async getMedia(id: Uuid): Promise<MediaAsset | null> {
        const { data } = await this.client.from('media_assets').select('*').eq('id', id).maybeSingle();
        return (data as MediaAsset) ?? null;
    }

    async createVideoUpload(moduleId: Uuid, title: string): Promise<{ mediaId: Uuid; uploadUrl: string }> {
        // The function inserts the media row with the caller's JWT, so RLS — not this
        // client — decides whether they may attach media to the module.
        const { data, error } = await this.client.functions.invoke('mux-upload', {
            body: { module_id: moduleId, title },
        });
        if (error) throw new DataSourceError(`Could not start the upload: ${error.message}`, error);
        return { mediaId: data.media_id, uploadUrl: data.upload_url };
    }

    async registerMuxPlayback(moduleId: Uuid, playbackId: string, title: string): Promise<MediaAsset> {
        return this.upsert<MediaAsset>('media_assets', {
            module_id: moduleId,
            kind: 'video',
            status: 'ready',
            mux_playback_id: playbackId.trim(),
            title,
        });
    }

    async uploadImage(moduleId: Uuid, file: File): Promise<MediaAsset> {
        const extension = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `${moduleId}/${crypto.randomUUID()}.${extension}`;
        const { error } = await this.client.storage.from('media').upload(path, file, {
            contentType: file.type || 'image/jpeg',
        });
        if (error) throw new DataSourceError(`Image upload failed: ${error.message}`, error);
        return this.upsert<MediaAsset>('media_assets', {
            module_id: moduleId,
            kind: 'image',
            status: 'ready',
            storage_path: path,
            title: file.name,
        });
    }

    imageUrl(storagePath: string): string {
        return this.client.storage.from('media').getPublicUrl(storagePath).data.publicUrl;
    }

    // --- Publishing --------------------------------------------------------------

    async validateModule(moduleId: Uuid): Promise<string[]> {
        const { data, error } = await this.client.rpc('validate_module', { p_module_id: moduleId });
        if (error) throw new DataSourceError(`validate: ${error.message}`, error);
        return (data as string[] | null) ?? [];
    }

    async publishModule(moduleId: Uuid, notes: string): Promise<number> {
        const { data, error } = await this.client.rpc('publish_module', {
            p_module_id: moduleId, p_notes: notes,
        });
        if (error) throw new DataSourceError(error.message, error);
        return data as number;
    }

    async listVersions(moduleId: Uuid): Promise<ModuleVersion[]> {
        return this.unwrap(
            await this.client
                .from('module_versions')
                .select('id, module_id, version, published_at, notes')
                .eq('module_id', moduleId)
                .order('version', { ascending: false }),
            'list versions',
        ) as ModuleVersion[];
    }

    async rollbackModule(moduleId: Uuid, version: number): Promise<void> {
        const { error } = await this.client.rpc('rollback_module', {
            p_module_id: moduleId, p_version: version,
        });
        if (error) throw new DataSourceError(error.message, error);
    }
}
